import type { AxiosRequestConfig } from 'axios';
import { utils } from '@/utils';
import { scopedApi } from '@/session/scopedClients';
import type { RequestScope } from '@/session/scopedTransport';
import type { ApiQueryParams } from '@/types/apiQuery';
import { EndpointResponse } from '@/types/endpoints';

export type ProductImage = {
  id: number;
  url: string;
  thumbnail?: string;
  position?: number;
  alt?: string | null;
  height?: number;
  width?: number;
  format?: string;
  type?: string;
};

export type Product = {
  id: number;
  name: string;
  sku: string;
  images?:
    | {
        default?: ProductImage[];
      }
    | ProductImage[]; // Support both nested and flat structure
  status?: string;
  description?: string;
  price?: number;
  created_at?: string;
  updated_at?: string;
  // Additional fields from catalog API
  group?: string;
  group_ids?: number[];
  variant_options?: Record<string, unknown>[];
  fields?: Record<string, unknown>;
  pricing_item?: Record<string, unknown>;
};

type RequestParams = {
  productIds: number[];
  limit?: Extract<ApiQueryParams['limit'], number>;
};

type Response = Product[];
export type ProductRequestOptions = Pick<AxiosRequestConfig, 'signal' | 'operationRequirement'>;

const validate = (productIds: number[]) => {
  if (!productIds || !Array.isArray(productIds)) {
    throw new Error('`productIds` is required and must be an array');
  }
  if (productIds.length === 0) {
    throw new Error('`productIds` array cannot be empty');
  }
};

/**
 * Fetch a single product by ID with full details including images
 * @deprecated Use getProducts with array of IDs for better performance
 */
export const getProductById = async (scope: RequestScope, productId: number, options: ProductRequestOptions = {}): Promise<Product | null> => {
  try {
    const response = await scopedApi(scope).get<EndpointResponse<Product>>(`/catalog/${productId}`, options);
    if (response.data && response.data.result) {
      return response.data.result;
    }
    return null;
  } catch (e) {
    console.error(`Error fetching product ${productId}:`, e);
    return null;
  }
};

/**
 * Fetch products using the $in operator with batching (includes images)
 * Batches requests to 50 products per request as per API limits
 */
export const getProducts = async (
  scope: RequestScope,
  { productIds, limit = 50 }: RequestParams,
  options: ProductRequestOptions = {}
): Promise<EndpointResponse<Response>> => {
  try {
    validate(productIds);

    // Batch productIds into chunks of 50 (API limit for $in queries)
    const batches: number[][] = [];
    for (let i = 0; i < productIds.length; i += limit) {
      batches.push(productIds.slice(i, i + limit));
    }

    // Fetch all batches in parallel
    const batchPromises = batches.map(async (batchIds) => {
      const query = {
        id: {
          $in: batchIds,
        },
      };

      const result = await scopedApi(scope).get<EndpointResponse<Response>>('/catalog', {
        ...options,
        params: {
          limit: limit,
          query: JSON.stringify(query),
        },
      });

      return result.data.result || [];
    });

    // Wait for all batches to complete
    const batchResults = await Promise.all(batchPromises);

    // Flatten results
    const allProducts = batchResults.flat();

    return {
      result: allProducts,
      status: 200,
      execution_time: 0,
      num_results: allProducts.length,
    };
  } catch (e) {
    return utils.error.handleAxiosError(e);
  }
};

/**
 * Fetch products individually with full details including images
 * @deprecated Use getProducts which now includes images and uses efficient batching
 */
export const getProductsWithDetails = async (
  scope: RequestScope,
  productIds: number[],
  concurrencyLimit = 5,
  options: ProductRequestOptions = {}
): Promise<Product[]> => {
  // Redirect to the new batch implementation
  const response = await getProducts(scope, { productIds }, options);
  if (response.isError) {
    return [];
  }
  return response.result ?? [];
};

/**
 * Legacy batch fetching function - redirects to new implementation
 * @deprecated Use getProducts instead
 */
export const getProductsInBatches = async (
  scope: RequestScope,
  productIds: number[],
  batchSize = 100,
  options: ProductRequestOptions = {}
): Promise<Product[]> => {
  // Redirect to the new batch implementation
  const response = await getProducts(scope, { productIds }, options);
  if (response.isError) {
    return [];
  }
  return response.result ?? [];
};
